/**
 * Membership Plans registry.
 *
 * A membership plan is data, not a branch. Each plan key maps to a descriptor that mirrors the
 * backend capability interfaces (AllocatesMonthlyHours, LimitsDailyHours, LimitsTimeWindow,
 * ForbidsWeekends, AllowsCrossFacilityBooking, Legacy), so adding a plan is one row here.
 *
 * Input is a membership period row from `customer.metafields.a9os.hours`:
 * { facility_id, start, end, hours, type, plan, daily_limit?, cross_facility? }
 * Values on the row always win over plan defaults — the backend configures them per facility.
 *
 * Required by membership-hours-utils.js and membership-slot-pricing.js; load it first.
 */
(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.MembershipPlans) return;

  const MINUTES_PER_HOUR = 60;
  const HOURS_PER_DAY = 24;
  const NOON_HOUR = 12;
  /** Every plan window so far opens at local midnight; only the end hour varies per plan. */
  const WINDOW_START_MINUTES = 0;
  /** Legacy rows may carry no type; only Unlimited ever had a pool this large. */
  const LEGACY_UNLIMITED_HOURS_THRESHOLD = 100;
  const UNLIMITED_WINDOW_END_HOUR = 17;
  const UNLIMITED_DAILY_LIMIT_HOURS = 3;
  const ELITE_DAILY_LIMIT_HOURS = 2;
  const EXECUTIVE_DAILY_LIMIT_HOURS = 4;
  const STARTER_TIER_PERCENT = 5;
  const PRO_TIER_PERCENT = 10;
  const ELITE_TIER_PERCENT = 15;
  const EXECUTIVE_TIER_PERCENT = 15;

  function describePlan(key, title, capabilities) {
    const caps = capabilities || {};
    return {
      key: key,
      title: title,
      tierPercent: caps.tierPercent != null ? caps.tierPercent : 0,
      hasHourPool: caps.hasHourPool !== false,
      dailyLimitHours: caps.dailyLimitHours != null ? caps.dailyLimitHours : null,
      windowEndHour: caps.windowEndHour != null ? caps.windowEndHour : null,
      forbidsWeekends: caps.forbidsWeekends === true,
      crossFacility: caps.crossFacility === true,
      legacy: caps.legacy === true
    };
  }

  const PLANS = {
    starter: describePlan('starter', 'Starter', { tierPercent: STARTER_TIER_PERCENT }),
    pro: describePlan('pro', 'Pro', { tierPercent: PRO_TIER_PERCENT }),
    elite: describePlan('elite', 'Elite', {
      dailyLimitHours: ELITE_DAILY_LIMIT_HOURS,
      crossFacility: true,
      tierPercent: ELITE_TIER_PERCENT
    }),
    executive: describePlan('executive', 'Executive', {
      dailyLimitHours: EXECUTIVE_DAILY_LIMIT_HOURS,
      crossFacility: true,
      tierPercent: EXECUTIVE_TIER_PERCENT
    }),
    unlimited: describePlan('unlimited', 'Unlimited', {
      hasHourPool: false,
      dailyLimitHours: UNLIMITED_DAILY_LIMIT_HOURS,
      windowEndHour: UNLIMITED_WINDOW_END_HOUR,
      forbidsWeekends: true,
      legacy: true
    })
  };

  /**
   * A plan key the theme does not know yet: behaves like a pool plan and takes every limit from
   * the row, so a plan added on the backend degrades instead of silently becoming Pro.
   */
  const UNKNOWN_PLAN = describePlan('unknown', 'Membership');

  function readPlanKey(entry) {
    return String((entry && entry.type) || '').trim().toLowerCase();
  }

  /** Descriptor for a period row, or null when there is no row. */
  function resolve(entry) {
    if (!entry) return null;
    const planKey = readPlanKey(entry);
    if (planKey && PLANS[planKey]) return PLANS[planKey];
    if (planKey) return UNKNOWN_PLAN;

    const hours = Number(entry.hours);
    if (Number.isFinite(hours) && hours >= LEGACY_UNLIMITED_HOURS_THRESHOLD) return PLANS.unlimited;
    return PLANS.pro;
  }

  function titleFor(entry) {
    const plan = resolve(entry);
    return plan ? plan.title : UNKNOWN_PLAN.title;
  }

  /** Plans without a pool (legacy Unlimited) never draw hours from a period balance. */
  function hasHourPool(entry) {
    const plan = resolve(entry);
    return plan ? plan.hasHourPool : true;
  }

  function forbidsWeekends(entry) {
    const plan = resolve(entry);
    return !!(plan && plan.forbidsWeekends);
  }

  function isLegacy(entry) {
    const plan = resolve(entry);
    return !!(plan && plan.legacy);
  }

  /** Elite/Executive apply network-wide; the row flag is authoritative. */
  function isCrossFacility(entry) {
    if (!entry) return false;
    if (entry.cross_facility === true) return true;
    const plan = resolve(entry);
    return !!(plan && plan.crossFacility);
  }

  /** Hours bookable per facility-local day, or null when the plan has no cap. */
  function dailyLimitFor(entry) {
    if (!entry) return null;
    const fromRow = Number(entry.daily_limit);
    if (Number.isFinite(fromRow) && fromRow > 0) return fromRow;
    const plan = resolve(entry);
    return plan && plan.dailyLimitHours != null ? plan.dailyLimitHours : null;
  }

  /** Latest wall-clock start minute the plan allows, or null when it has no window. */
  function windowEndMinutesFor(entry) {
    const plan = resolve(entry);
    if (!plan || plan.windowEndHour == null) return null;
    return plan.windowEndHour * MINUTES_PER_HOUR;
  }

  function formatClockLabel(minutes) {
    const hour24 = Math.floor(minutes / MINUTES_PER_HOUR) % HOURS_PER_DAY;
    const minute = minutes % MINUTES_PER_HOUR;
    const suffix = hour24 < NOON_HOUR ? 'AM' : 'PM';
    const hour12 = hour24 % NOON_HOUR === 0 ? NOON_HOUR : hour24 % NOON_HOUR;
    return minute === 0
      ? `${hour12}${suffix}`
      : `${hour12}:${String(minute).padStart(2, '0')}${suffix}`;
  }

  /**
   * The plan's booking window as display text ("12AM-5PM"), or '' when it has none.
   * Lives here so the copy can never drift from the window the calculation actually enforces.
   */
  function windowLabelFor(entry) {
    const endMinutes = windowEndMinutesFor(entry);
    if (endMinutes == null) return '';
    return `${formatClockLabel(WINDOW_START_MINUTES)}-${formatClockLabel(endMinutes)}`;
  }

  /** Plan keys in declaration order, sellable ones only. */
  function sellableKeys() {
    return Object.keys(PLANS).filter((key) => !PLANS[key].legacy);
  }

  /** Plan keys that exist but are no longer sold. */
  function legacyKeys() {
    return Object.keys(PLANS).filter((key) => PLANS[key].legacy);
  }

  const api = {
    resolve: resolve,
    titleFor: titleFor,
    sellableKeys: sellableKeys,
    legacyKeys: legacyKeys,
    hasHourPool: hasHourPool,
    forbidsWeekends: forbidsWeekends,
    isLegacy: isLegacy,
    isCrossFacility: isCrossFacility,
    dailyLimitFor: dailyLimitFor,
    windowEndMinutesFor: windowEndMinutesFor,
    windowLabelFor: windowLabelFor,
    LEGACY_UNLIMITED_HOURS_THRESHOLD: LEGACY_UNLIMITED_HOURS_THRESHOLD
  };

  if (typeof window !== 'undefined') window.MembershipPlans = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
